import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';

export const MATOMO_GOALS_FOR_CUSTOMER_QUERY = gql`
  query MatomoGoalsForCustomer($customerId: ID!) {
    customer(id: $customerId) {
      id
      websites {
        id
        title
        url
        matomoId
        matomoGoals {
          id
          matomoGoalId
          name
        }
      }
    }
  }
`;

export function parseMatomoGoalCompositeId(goalId) {
  if (goalId == null || goalId === '') {
    return null;
  }

  const [idSite, idGoal] = String(goalId).split('-');
  if (!idSite || idGoal == null || idGoal === '') {
    return null;
  }

  return {
    idSite: Number(idSite),
    idGoal: Number(idGoal),
  };
}

export function normalizeMatomoGoalCompositeId(value) {
  if (value == null || value === '') {
    return undefined;
  }

  const id = String(value);
  return parseMatomoGoalCompositeId(id) ? id : undefined;
}

export function isDeletedMatomoGoal(goalById, value, loading = false) {
  if (value == null || value === '' || loading) {
    return false;
  }

  return !goalById?.[String(value)];
}

export function formatDeletedGoalLabel(label, { html = false } = {}) {
  return html ? `<em>${label}</em>` : label;
}

export function getMatomoGoalLabel(goalById, value, { deletedLabel = 'Deleted goal', loading = false } = {}) {
  if (value == null || value === '') {
    return value;
  }

  const id = String(value);
  const name = goalById?.[id];
  if (name) {
    return name;
  }

  if (loading) {
    return undefined;
  }

  return deletedLabel;
}

export function collectConversionGoalIds(query) {
  if (!query?.rules?.length) {
    return [];
  }

  const ids = new Set();

  const visit = (rules) => {
    for (const rule of rules) {
      if (rule.rules) {
        visit(rule.rules);
      } else if (rule.field === 'conversionGoalId' && rule.value != null && rule.value !== '') {
        ids.add(String(rule.value));
      }
    }
  };

  visit(query.rules);
  return [...ids];
}

const GOALS_BY_IDS_QUERY = gql`
  query MatomoGoalsByIds($ids: [ID!]!) {
    matomoGoalsByIds(ids: $ids) {
      id
      name
    }
  }
`;

export function useMatomoGoalNamesByIds(ids) {
  const { data, loading } = useQuery(GOALS_BY_IDS_QUERY, {
    variables: { ids },
    skip: !ids?.length,
  });

  const goalById = useMemo(() => {
    const map = {};
    for (const goal of data?.matomoGoalsByIds || []) {
      map[goal.id] = goal.name;
    }
    return map;
  }, [data]);

  return { goalById, loading };
}

export default function useMatomoGoalOptions(customerId) {
  const { data, loading } = useQuery(MATOMO_GOALS_FOR_CUSTOMER_QUERY, {
    variables: { customerId },
    skip: !customerId,
  });

  return useMemo(() => {
    const goalById = {};
    const options = [];
    const groupedOptions = [];

    for (const website of data?.customer?.websites || []) {
      const websiteGoals = (website.matomoGoals || [])
        .map((goal) => {
          goalById[goal.id] = goal.name;
          return { label: goal.name, value: goal.id };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      if (websiteGoals.length) {
        options.push(...websiteGoals);
        groupedOptions.push({
          label: website.title || website.url,
          options: websiteGoals,
        });
      }
    }

    groupedOptions.sort((a, b) => a.label.localeCompare(b.label));

    return { options, groupedOptions, goalById, loading };
  }, [data]);
}
